import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe,
  TrendingUp,
  BarChart3,
  ArrowRight,
  Star,
  Flame,
  Film,
  Tv,
  Sparkles,
} from 'lucide-react';
import SearchHero from '../../components/SearchHero';
import { Container } from '../../components/styled/Container';
import { PageLayout } from '../../components/styled/PageLayout';
import { fetchCatalog } from '../../store/actions/catalogActions';
import { buildHomeTabRowsFromBrowseCards, formatCatalogCount } from '../../utils/catalogBrowse';
import { similarPath } from '../../utils/paths';
import { regionFromPathname } from '../../utils/regionFromPath';
import { categoriesHubPath, similarTypePath } from '../../utils/categoriesPaths';
import SEO from '../../components/seo/SEO';
import { useI18n } from '../../hooks/useI18n';
import { useAdDevice } from '../../hooks/useAdDevice';
import StaticAdBanner from '../../components/ads/StaticAdBanner';
import AdCarousel from '../../components/ads/AdCarousel';
import PromoAdCard from '../../components/ads/PromoAdCard';
import AdPopup from '../../components/ads/AdPopup';
import { bannerAds, carouselAds, cardAds, sidebarAds } from '../../data/staticAds';
import { SIMIMOVIES_MANAGED_ADS_ENABLED } from '../../config/ads';
import { fetchAdPlacement, trackAdClick, trackAdImpression } from '../../store/actions/adActions';
import { selectAdPlacementFirst, selectAdPlacementList } from '../../store/selectors/adSelectors';
import { managedPlacementToAd } from '../../utils/adMapper';
import { makeImpressionKey, shouldTrackImpression } from '../../utils/adTracking';

const fallbackStats = [
  { icon: Globe, label: 'Titles in catalog', value: '0', color: 'primary' },
  { icon: TrendingUp, label: 'Movies', value: '0', color: 'crimson' },
  { icon: BarChart3, label: 'TV & anime', value: '0', color: 'gold' },
];

function initialTabRows() {
  return {
    movies: { key: 'movies', title: 'Movies', subtitle: 'Latest movie picks', items: [] },
    tv: { key: 'tv', title: 'TV shows', subtitle: 'Current TV picks', items: [] },
    anime: { key: 'anime', title: 'Anime', subtitle: 'Anime movie and series picks', items: [] },
  };
}

const TAB_KEYS = ['movies', 'tv', 'anime'];

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const StatCard = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCard};
`;

const StatIconBox = styled.div`
  width: 2.75rem;
  height: 2.75rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $color, theme }) => {
    if ($color === 'primary') return 'rgba(0,48,135,0.1)';
    if ($color === 'crimson') return 'rgba(220,38,38,0.1)';
    return 'rgba(234,179,8,0.1)';
  }};
  color: ${({ $color, theme }) => {
    if ($color === 'primary') return theme.colors.primary;
    if ($color === 'crimson') return theme.colors.crimson;
    return theme.colors.gold;
  }};
`;

const StatValue = styled.p`
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontClamp['2xl']};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const StatLabel = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0;
`;

const Section = styled.section`
  padding: 2.5rem 0;
  width: 100%;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontClamp['2xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const SectionLink = styled.a`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.crimson};
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
  }
`;

const CatPageLink = styled(Link)`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-left: 0.75rem;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const TabSection = styled.div`
  width: 100%;
`;

const TabList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  margin: 0 0 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  padding: 0.35rem;
  width: 100%;
  box-sizing: border-box;
  @media (min-width: 640px) {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.35rem;
    padding: 0.4rem;
  }
`;

const TabButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: calc(${({ theme }) => theme.radiusSm} - 4px);
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
  flex: 1;
  min-width: 0;
  background: ${({ $active, theme }) => ($active ? theme.colors.maroon : 'transparent')};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.maroonForeground : theme.colors.foreground};
  box-shadow: ${({ $active, theme }) => ($active ? theme.shadowCard : 'none')};
  &:hover {
    background: ${({ $active, theme }) =>
      $active ? theme.colors.maroon : theme.colors.card};
    color: ${({ $active, theme }) =>
      $active ? theme.colors.maroonForeground : theme.colors.primary};
  }
`;

const TabPanelIntro = styled.p`
  margin: 0 0 1.5rem;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  line-height: 1.55;
  max-width: 52rem;
`;

const SitesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  width: 100%;
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (min-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media (min-width: 1600px) {
    grid-template-columns: repeat(5, 1fr);
  }
`;

const Card = styled(motion.div)`
  background: ${({ theme }) => theme.colors.card};
  padding: 1.25rem 1.35rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  box-shadow: ${({ theme }) => theme.shadowCard};
  transition: box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  min-width: 0;
  &:hover {
    box-shadow: ${({ theme }) => theme.shadowCardHover};
  }
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
`;

const PosterThumb = styled.div`
  width: 3.25rem;
  height: 3.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(0, 48, 135, 0.1);
  background-image: ${({ $url }) => ($url ? `url(${$url})` : 'none')};
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontClamp.lg};
`;

const ScoreBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.65rem;
  background: rgba(234, 179, 8, 0.1);
  color: ${({ theme }) => theme.colors.gold};
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
  flex-shrink: 0;
`;

const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontClamp.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
  line-height: 1.25;
`;

const CardDesc = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
`;

const TagsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Tag = styled.span`
  padding: 0.25rem 0.65rem;
  background: rgba(128, 0, 0, 0.1);
  color: ${({ theme }) => theme.colors.maroon};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 500;
  border-radius: ${({ theme }) => theme.radiusSm};
`;

const CardLink = styled(Link)`
  width: 100%;
  padding: 0.7rem 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-decoration: none;
  transition: opacity 0.2s;
  margin-top: auto;
  &:hover {
    opacity: 0.9;
  }
`;

const CtaBanner = styled.div`
  background: ${({ theme }) => theme.colors.maroon};
  border-radius: ${({ theme }) => theme.radiusSm};
  padding: 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  @media (min-width: 768px) {
    padding: 3.5rem;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const CtaTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontClamp['2xl']};
  font-weight: 800;
  color: ${({ theme }) => theme.colors.maroonForeground};
  margin: 0;
  @media (min-width: 768px) {
    font-size: ${({ theme }) => theme.fontClamp['3xl']};
  }
`;

const CtaDesc = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: rgba(255, 255, 255, 0.7);
  margin: 0.5rem 0 0 0;
  max-width: 36rem;
`;

const CtaBtn = styled(Link)`
  padding: 1rem 2rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.gold};
  color: ${({ theme }) => theme.colors.goldForeground};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  text-decoration: none;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`;

const TopList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
`;

const PromoGridHome = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  width: 100%;
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const TopRow = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1rem 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCard};
  text-decoration: none;
  color: inherit;
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: ${({ theme }) => theme.shadowCardHover};
  }
`;

const RankNum = styled.span`
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontClamp.lg};
  font-weight: 700;
  width: 2rem;
  text-align: right;
  flex-shrink: 0;
  color: ${({ $gold, theme }) => ($gold ? theme.colors.gold : theme.colors.mutedForeground)};
`;

const TopPoster = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(0, 48, 135, 0.1);
  background-size: cover;
  background-position: center;
  background-image: ${({ $url }) => ($url ? `url(${$url})` : 'none')};
  flex-shrink: 0;
`;

const TopInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const TopTitle = styled.h3`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  margin: 0;
`;

const TopSub = styled.span`
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const TrendingPill = styled.span`
  padding: 0.2rem 0.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 600;
  flex-shrink: 0;
  background: ${({ $rising, $top }) => ($top ? 'rgba(234,179,8,0.15)' : $rising ? 'rgba(34,197,94,0.15)' : 'rgba(0,0,0,0.06)')};
  color: ${({ $rising, $top, theme }) => ($top ? theme.colors.gold : $rising ? 'hsl(142,71%,45%)' : theme.colors.mutedForeground)};
`;

const HeaderWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const IconBox = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ $crimson, $gold }) => ($crimson ? 'rgba(220,38,38,0.1)' : $gold ? 'rgba(234,179,8,0.1)' : 'transparent')};
  color: ${({ $crimson, $gold, theme }) => ($crimson ? theme.colors.crimson : $gold ? theme.colors.gold : 'inherit')};
`;

function typeLabel(cat) {
  if (cat === 'anime_tv' || cat === 'anime_movie') return 'Anime';
  if (cat === 'tv') return 'TV';
  return 'Movie';
}

export default function Home() {
  const dispatch = useDispatch();
  const { items: catalogItems, counts: heroCounts } = useSelector((s) => s.catalog);
  const { pathname, hash } = useLocation();
  const region = regionFromPathname(pathname);
  const pre = region || '';
  const base = pre ? `/${pre}` : '';
  const { t, locale } = useI18n();

  const [browseTab, setBrowseTab] = useState('movies');

  const adDomain = useMemo(() => (typeof window !== 'undefined' ? window.location.hostname : ''), []);
  const adLocale = (locale || 'en').toLowerCase();
  const adDevice = useAdDevice();

  const managedTopBanner = useSelector((s) => selectAdPlacementFirst(s, 'home', 'top-banner'));
  const managedBottomBanner = useSelector((s) => selectAdPlacementFirst(s, 'home', 'bottom-banner'));
  const managedCarousel = useSelector((s) => selectAdPlacementList(s, 'home', 'carousel'));
  const managedCards = useSelector((s) => selectAdPlacementList(s, 'home', 'cards'));
  const managedPopup = useSelector((s) => selectAdPlacementFirst(s, 'home', 'popup'));

  useEffect(() => {
    if (!SIMIMOVIES_MANAGED_ADS_ENABLED) return;
    const baseParams = { device: adDevice, domain: adDomain, locale: adLocale };
    dispatch(
      fetchAdPlacement({
        page: 'home',
        placement: 'top-banner',
        params: { ...baseParams, page: 'home', placement: 'top-banner', limit: 1 },
      }),
    );
    dispatch(
      fetchAdPlacement({
        page: 'home',
        placement: 'bottom-banner',
        params: { ...baseParams, page: 'home', placement: 'bottom-banner', limit: 1 },
      }),
    );
    dispatch(
      fetchAdPlacement({
        page: 'home',
        placement: 'carousel',
        params: { ...baseParams, page: 'home', placement: 'carousel', limit: 6 },
      }),
    );
    dispatch(
      fetchAdPlacement({
        page: 'home',
        placement: 'cards',
        params: { ...baseParams, page: 'home', placement: 'cards', limit: 2 },
      }),
    );
    dispatch(
      fetchAdPlacement({
        page: 'home',
        placement: 'popup',
        params: { ...baseParams, page: 'home', placement: 'popup', limit: 1 },
      }),
    );
  }, [dispatch, adDevice, adDomain, adLocale]);

  useEffect(() => {
    if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !managedTopBanner?._id) return;
    const key = makeImpressionKey({
      campaign: managedTopBanner._id,
      page: 'home',
      placement: 'top-banner',
      device: adDevice,
      domain: adDomain,
    });
    if (shouldTrackImpression(key)) {
      dispatch(
        trackAdImpression({
          campaign: managedTopBanner._id,
          page: 'home',
          placement: 'top-banner',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    }
  }, [dispatch, managedTopBanner, adDevice, adDomain, adLocale]);

  useEffect(() => {
    if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !managedBottomBanner?._id) return;
    const key = makeImpressionKey({
      campaign: managedBottomBanner._id,
      page: 'home',
      placement: 'bottom-banner',
      device: adDevice,
      domain: adDomain,
    });
    if (shouldTrackImpression(key)) {
      dispatch(
        trackAdImpression({
          campaign: managedBottomBanner._id,
          page: 'home',
          placement: 'bottom-banner',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    }
  }, [dispatch, managedBottomBanner, adDevice, adDomain, adLocale]);

  useEffect(() => {
    if (!SIMIMOVIES_MANAGED_ADS_ENABLED) return;
    managedCards.forEach((item) => {
      if (!item?._id) return;
      const key = makeImpressionKey({
        campaign: item._id,
        page: 'home',
        placement: 'cards',
        device: adDevice,
        domain: adDomain,
      });
      if (shouldTrackImpression(key)) {
        dispatch(
          trackAdImpression({
            campaign: item._id,
            page: 'home',
            placement: 'cards',
            device: adDevice,
            domain: adDomain,
            locale: adLocale,
          }),
        );
      }
    });
  }, [dispatch, managedCards, adDevice, adDomain, adLocale]);

  const topBannerAd = useMemo(() => managedPlacementToAd(managedTopBanner, bannerAds[0]), [managedTopBanner]);
  const bottomBannerAd = useMemo(() => managedPlacementToAd(managedBottomBanner, bannerAds[1] ?? bannerAds[0]), [managedBottomBanner]);

  const homeCarouselAds = useMemo(
    () =>
      managedCarousel.length
        ? managedCarousel.map((x, i) => managedPlacementToAd(x, carouselAds[i] || carouselAds[0]))
        : carouselAds,
    [managedCarousel],
  );

  const homeCardAds = useMemo(
    () =>
      managedCards.length ? managedCards.map((x, i) => managedPlacementToAd(x, cardAds[i] || cardAds[0])) : cardAds,
    [managedCards],
  );

  const popupAd = useMemo(() => managedPlacementToAd(managedPopup, sidebarAds[0]), [managedPopup]);

  const onHomeCarouselVisible = useCallback(
    (visibleAd) => {
      if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !visibleAd?.managedCampaignId) return;
      const key = makeImpressionKey({
        campaign: visibleAd.managedCampaignId,
        page: 'home',
        placement: 'carousel',
        device: adDevice,
        domain: adDomain,
      });
      if (shouldTrackImpression(key)) {
        dispatch(
          trackAdImpression({
            campaign: visibleAd.managedCampaignId,
            page: 'home',
            placement: 'carousel',
            device: adDevice,
            domain: adDomain,
            locale: adLocale,
          }),
        );
      }
    },
    [dispatch, adDevice, adDomain, adLocale],
  );

  const onHomeCarouselClick = useCallback(
    (_e, visibleAd) => {
      if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !visibleAd?.managedCampaignId) return;
      dispatch(
        trackAdClick({
          campaign: visibleAd.managedCampaignId,
          page: 'home',
          placement: 'carousel',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    },
    [dispatch, adDevice, adDomain, adLocale],
  );

  const onHomePopupVisible = useCallback(
    (ad) => {
      if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !ad?.managedCampaignId) return;
      const key = makeImpressionKey({
        campaign: ad.managedCampaignId,
        page: 'home',
        placement: 'popup',
        device: adDevice,
        domain: adDomain,
      });
      if (shouldTrackImpression(key)) {
        dispatch(
          trackAdImpression({
            campaign: ad.managedCampaignId,
            page: 'home',
            placement: 'popup',
            device: adDevice,
            domain: adDomain,
            locale: adLocale,
          }),
        );
      }
    },
    [dispatch, adDevice, adDomain, adLocale],
  );

  const onHomePopupClick = useCallback(
    (_e, ad) => {
      if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !ad?.managedCampaignId) return;
      dispatch(
        trackAdClick({
          campaign: ad.managedCampaignId,
          page: 'home',
          placement: 'popup',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    },
    [dispatch, adDevice, adDomain, adLocale],
  );

  const onTopBannerClick = useCallback(
    () => {
      if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !managedTopBanner?._id) return;
      dispatch(
        trackAdClick({
          campaign: managedTopBanner._id,
          page: 'home',
          placement: 'top-banner',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    },
    [dispatch, managedTopBanner, adDevice, adDomain, adLocale],
  );

  const onBottomBannerClick = useCallback(
    () => {
      if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !managedBottomBanner?._id) return;
      dispatch(
        trackAdClick({
          campaign: managedBottomBanner._id,
          page: 'home',
          placement: 'bottom-banner',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    },
    [dispatch, managedBottomBanner, adDevice, adDomain, adLocale],
  );

  const cardClickHandlers = useMemo(() => {
    return homeCardAds.map((ad) =>
      ad?.managedCampaignId
        ? () => {
            if (!SIMIMOVIES_MANAGED_ADS_ENABLED) return;
            dispatch(
              trackAdClick({
                campaign: ad.managedCampaignId,
                page: 'home',
                placement: 'cards',
                device: adDevice,
                domain: adDomain,
                locale: adLocale,
              }),
            );
          }
        : undefined,
    );
  }, [dispatch, homeCardAds, adDevice, adDomain, adLocale]);

  useEffect(() => {
    dispatch(
      fetchCatalog({
        limit: 120,
        counts: 1,
        region: pre || undefined,
        clearPrevious: true,
      }),
    );
  }, [dispatch, pre]);

  const rowByKey = useMemo(() => {
    if (!catalogItems?.length) return initialTabRows();
    return buildHomeTabRowsFromBrowseCards(catalogItems);
  }, [catalogItems]);

  const displayStats = useMemo(() => {
    if (!heroCounts) return fallbackStats;
    return [
      { icon: Globe, label: 'Titles in catalog', value: formatCatalogCount(heroCounts.total), color: 'primary' },
      { icon: TrendingUp, label: 'Movies', value: formatCatalogCount(heroCounts.movies), color: 'crimson' },
      {
        icon: BarChart3,
        label: 'TV & anime',
        value: formatCatalogCount((heroCounts.tv || 0) + (heroCounts.anime || 0)),
        color: 'gold',
      },
    ];
  }, [heroCounts]);

  const trendingPicks = useMemo(() => {
    const flat = [
      ...(rowByKey.movies?.items || []),
      ...(rowByKey.tv?.items || []),
      ...(rowByKey.anime?.items || []),
    ];
    return flat.slice(0, 6);
  }, [rowByKey]);

  const allBrowseFlat = useMemo(() => {
    return [
      ...(rowByKey.movies?.items || []),
      ...(rowByKey.tv?.items || []),
      ...(rowByKey.anime?.items || []),
    ];
  }, [rowByKey]);

  useEffect(() => {
    const key = (hash || (typeof window !== 'undefined' ? window.location.hash : '') || '')
      .replace('#', '')
      .toLowerCase();
    if (TAB_KEYS.includes(key)) setBrowseTab(key);
  }, [hash, pathname]);

  const setTab = (key) => {
    setBrowseTab(key);
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', `${base || '/'}#${key}`);
    }
  };

  const activeRow = rowByKey[browseTab];

  const tabItems = useMemo(() => activeRow?.items || [], [activeRow]);

  return (
    <>
      {/*
        WebSite schema improves brand/query understanding for home SEO.
      */}
      <SEO
        region={region}
        path="/"
        title={t('home.meta.title', 'FliqMatch - Find Similar Movies, TV Shows and Anime')}
        description={t('home.meta.description', 'Discover similar picks by type, browse curated matches, and vote with the community.')}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'FliqMatch',
          url: '/',
          inLanguage: region || 'en',
          potentialAction: {
            '@type': 'SearchAction',
            target: '/search?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      <SearchHero />
      <PageLayout>
        <Section style={{ paddingTop: '2.5rem' }}>
          <Container>
            <StatGrid>
              {displayStats.map((stat) => (
                <StatCard
                  key={stat.label}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <StatIconBox $color={stat.color}>
                    <stat.icon size={20} strokeWidth={2.5} />
                  </StatIconBox>
                  <div>
                    <StatValue>{stat.value}</StatValue>
                    <StatLabel>{stat.label}</StatLabel>
                  </div>
                </StatCard>
              ))}
            </StatGrid>
          </Container>
        </Section>

        <Section>
          <Container>
            <StaticAdBanner ad={topBannerAd} pageKey="home" onLinkClick={onTopBannerClick} />
          </Container>
        </Section>

        <Section id="browse-tabs">
          <Container>
            <SectionHeader>
              <div>
                <SectionTitle>{t('home.browse.title', 'Browse titles — see similar picks')}</SectionTitle>
                <p
                  style={{
                    margin: '0.5rem 0 0',
                    fontSize: '0.9rem',
                    color: 'var(--muted-foreground)',
                    maxWidth: '48rem',
                  }}
                >
                  {t('home.browse.lead', 'Choose a type, pick a title, then open similar movies, shows, or anime.')}
                </p>
              </div>
              <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.25rem' }}>
                <CatPageLink to={categoriesHubPath(pre)}>
                  {t('home.allCategories', 'All categories')} <ArrowRight size={14} />
                </CatPageLink>
                <SectionLink href={`${base || '/'}#trending-similar`}>
                  {t('home.trending', 'Trending')} <ArrowRight size={14} />
                </SectionLink>
              </span>
            </SectionHeader>

            <TabSection>
              <TabList role="tablist" aria-label={t('home.browse.aria', 'Browse by type')}>
                <TabButton
                  type="button"
                  role="tab"
                  aria-selected={browseTab === 'movies'}
                  $active={browseTab === 'movies'}
                  onClick={() => setTab('movies')}
                >
                  <Film size={18} strokeWidth={2.5} />
                  {t('explore.movies', 'Movies')}
                </TabButton>
                <TabButton
                  type="button"
                  role="tab"
                  aria-selected={browseTab === 'tv'}
                  $active={browseTab === 'tv'}
                  onClick={() => setTab('tv')}
                >
                  <Tv size={18} strokeWidth={2.5} />
                  {t('explore.tv', 'TV shows')}
                </TabButton>
                <TabButton
                  type="button"
                  role="tab"
                  aria-selected={browseTab === 'anime'}
                  $active={browseTab === 'anime'}
                  onClick={() => setTab('anime')}
                >
                  <Sparkles size={18} strokeWidth={2.5} />
                  {t('explore.anime', 'Anime')}
                </TabButton>
              </TabList>

              <AnimatePresence mode="wait">
                <motion.div
                  key={browseTab}
                  role="tabpanel"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.2 }}
                >
                  <TabPanelIntro>
                    <strong>{activeRow?.title}</strong> — {activeRow?.subtitle}. {t('home.browse.tap', 'Tap')} <strong>{t('similar.browse.seePicks', 'See similar picks')}</strong> {t('home.browse.forAny', 'for any card.')}{' '}
                    <CatPageLink to={similarTypePath(browseTab, pre)} style={{ marginLeft: 0 }}>
                      {t('home.browse.openCategory', 'Open full category page')} <ArrowRight size={14} />
                    </CatPageLink>
                  </TabPanelIntro>
                  <SitesGrid>
              {tabItems.length === 0 ? (
                <p style={{ gridColumn: '1 / -1', color: 'var(--muted-foreground)' }}>
                  {t('home.browse.empty', 'No titles available for this section yet.')}
                </p>
              ) : null}
                    {tabItems.map((it, i) => (
                      <Card
                        key={`${it.category}-${it.tmdbId}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: Math.min(i * 0.04, 0.24) }}
                        whileHover={{ y: -3 }}
                      >
                        <CardTop>
                          <PosterThumb $url={it.poster}>{it.poster ? '' : it.title.charAt(0)}</PosterThumb>
                          <ScoreBadge>
                            <Star size={12} fill="currentColor" />
                            {typeLabel(it.category)}
                          </ScoreBadge>
                        </CardTop>
                        <CardTitle>{it.title}</CardTitle>
                        <CardDesc>{it.overview}</CardDesc>
                        <TagsWrap>
                          <Tag>{typeLabel(it.category)}</Tag>
                          <Tag>{t('similar.browse.similarBadge', 'Similar')}</Tag>
                        </TagsWrap>
                        <CardLink to={similarPath(it.category, it.tmdbId, it.title, pre)}>
                          {t('similar.browse.seePicks', 'See similar picks')} <ArrowRight size={14} />
                        </CardLink>
                      </Card>
                    ))}
                  </SitesGrid>
                </motion.div>
              </AnimatePresence>
            </TabSection>
          </Container>
        </Section>

        <Section>
          <Container>
            <AdCarousel
              ads={homeCarouselAds}
              pageKey="home"
              onVisibleAd={onHomeCarouselVisible}
              onAdClick={onHomeCarouselClick}
            />
          </Container>
        </Section>

        <Section id="trending-similar">
          <Container>
            <SectionHeader>
              <HeaderWithIcon>
                <IconBox $crimson>
                  <Flame size={20} strokeWidth={2.5} />
                </IconBox>
                <SectionTitle>{t('home.trending.title', 'Trending — find similar')}</SectionTitle>
              </HeaderWithIcon>
            </SectionHeader>
            <SitesGrid>
              {trendingPicks.map((it) => (
                <Card
                  key={`t-${it.category}-${it.tmdbId}`}
                  whileHover={{ y: -3 }}
                  transition={{ duration: 0.2 }}
                >
                  <CardTop>
                    <PosterThumb $url={it.poster} />
                    <ScoreBadge>
                      <Star size={12} fill="currentColor" /> {t('home.hot', 'Hot')}
                    </ScoreBadge>
                  </CardTop>
                  <CardTitle>{it.title}</CardTitle>
                  <CardDesc>{it.overview}</CardDesc>
                  <TagsWrap>
                    <Tag>{typeLabel(it.category)}</Tag>
                  </TagsWrap>
                  <CardLink to={similarPath(it.category, it.tmdbId, it.title, pre)}>
                    {t('similar.browse.seePicks', 'See similar picks')} <ArrowRight size={14} />
                  </CardLink>
                </Card>
              ))}
            </SitesGrid>
          </Container>
        </Section>

        <Section>
          <Container>
            <PromoGridHome>
              {homeCardAds.map((ad, i) => (
                <PromoAdCard key={ad.id || `card-${i}`} ad={ad} pageKey="home" onLinkClick={cardClickHandlers[i]} />
              ))}
            </PromoGridHome>
          </Container>
        </Section>

        <Section>
          <Container>
            <StaticAdBanner ad={bottomBannerAd} pageKey="home" onLinkClick={onBottomBannerClick} />
          </Container>
        </Section>

        <Section>
          <Container>
            <CtaBanner>
              <div>
                <CtaTitle>{t('home.cta.title', 'Help others find the next great match')}</CtaTitle>
                <CtaDesc>
                  {t('home.cta.desc', 'On any similar page, rate how close each suggestion feels or suggest a title we missed.')}
                </CtaDesc>
              </div>
              <CtaBtn to={`${base}/search`}>
                {t('home.cta.searchTitle', 'Search a title')} <ArrowRight size={16} />
              </CtaBtn>
            </CtaBanner>
          </Container>
        </Section>

        <Section>
          <Container>
            <SectionHeader>
              <HeaderWithIcon>
                <IconBox $gold>
                  <Star size={20} strokeWidth={2.5} />
                </IconBox>
                <SectionTitle>{t('home.topLookups', 'Top similar lookups')}</SectionTitle>
              </HeaderWithIcon>
            </SectionHeader>
            <TopList>
              {trendingPicks.slice(0, 6).map((it, i) => {
                return (
                  <motion.div
                    key={`${it.category}-${it.tmdbId}`}
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <TopRow to={similarPath(it.category, it.tmdbId, it.title, pre)}>
                      <RankNum $gold={i === 0}>{i + 1}</RankNum>
                      <TopPoster $url={it.poster} />
                      <TopInfo>
                        <TopTitle>{it.title}</TopTitle>
                        <TopSub>{typeLabel(it.category)} · {t('similar.browse.similarBadge', 'Similar')}</TopSub>
                      </TopInfo>
                      <TrendingPill $top={i === 0}>{t('home.topPick', 'Top pick')}</TrendingPill>
                    </TopRow>
                  </motion.div>
                );
              })}
              {trendingPicks.length === 0 ? (
                <p style={{ color: 'var(--muted-foreground)' }}>{t('home.topLookupsEmpty', 'No lookup trends available yet.')}</p>
              ) : null}
            </TopList>
          </Container>
        </Section>
      </PageLayout>
      <AdPopup
        ad={popupAd}
        delay={10000}
        pageKey="home"
        onVisible={onHomePopupVisible}
        onCtaClick={onHomePopupClick}
      />
    </>
  );
}
